import { Link as ILink, LinkProps as ILinkProps } from 'react-router';

import { ReactNode } from 'react';

/**
 * Custom props interface for the Link component, extending react-router's LinkProps
 * while omitting the 'href' prop and adding custom properties
 */
type CustomLinkProps = Omit<ILinkProps, 'href'> & {
	/** The destination URL or path for the link */
	to?: string;
	/** Alternative prop for destination URL (mapped to 'to' internally) */
	href?: string;
	/** React node(s) to be rendered as the link content */
	children?: ReactNode;
	/** CSS class name(s) to apply to the link element */
	className?: string;
	/** ARIA role attribute for accessibility */
	role?: string;
	/** React ref object for accessing the underlying anchor element */
	ref?: React.RefObject<HTMLAnchorElement>;
};

/**
 * A custom Link component that wraps react-router's Link component
 * 
 * This component provides a unified interface for navigation links, supporting both
 * 'to' and 'href' props for destination URLs. It forwards all react-router Link
 * functionality while adding flexibility for different prop naming conventions.
 * 
 * @param props - The props object containing link configuration
 * @param props.to - The destination URL or path for the link
 * @param props.href - Alternative destination URL (takes precedence over 'to' if both are provided)
 * @param props.children - The content to render inside the link
 * @param props.className - CSS classes to apply to the link element
 * @param props.role - ARIA role for accessibility
 * @param props.ref - React ref for accessing the DOM element
 * @param props.rest - Additional props passed through to react-router's Link component
 * 
 * @returns A react-router Link component with the specified props
 * 
 * @example
 * ```tsx
 * // Using 'to' prop
 * <Link to="/dashboard">Go to Dashboard</Link>
 * 
 * // Using 'href' prop
 * <Link href="/profile" className="nav-link">Profile</Link>
 * 
 * // With additional props
 * <Link to="/help" role="button" className="help-link">
 *   Help Center
 * </Link>
 * ```
 */
function Link(props: CustomLinkProps) {
	const { ref, to, href, children, className, role, ...rest } = props;

	return (
		<ILink
			className={className}
			to={to || href}
			role={role}
			ref={ref}
			{...rest}
		>
			{children}
		</ILink>
	);
}

export default Link;
